import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Container, Grid, CircularProgress, TextField } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import directoryService, { UserProfileCardData } from "../../services/directoryService";
import { USER_DIRECTORY_PAGE_SIZE } from "../../constants/directoryConstants";
import ProfileCard from "../../components/directory/ProfileCard/ProfileCard";
import AlphabetNavigator from "../../components/directory/AlphabetNavigator/AlphabetNavigator";
import InfiniteScroll from "react-infinite-scroll-component";
import classes from "./UserDirectoryPage.module.scss";
import useDebounce from "../../hooks/useDebounce";

const UserDirectoryPage: React.FC = () => {
    const [users, setUsers] = useState<UserProfileCardData[]>([]);
    const [selectedLetter, setSelectedLetter] = useState("A");
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const loaderRef = useRef<HTMLDivElement | null>(null);

    // Function to fetch users, now handling search
    const fetchUsers = useCallback(
        async (letter: string, isNewFilter: boolean, search: string) => {
            // No need to fetch if we are searching but there is no search term
            if (isNewFilter && search.length === 0 && letter.length === 0) {
                setUsers([]);
                setHasNextPage(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const response = await directoryService.fetchDirectoryUsers(
                    letter,
                    USER_DIRECTORY_PAGE_SIZE,
                    isNewFilter ? undefined : lastEvaluatedKey,
                    search,
                );
                setUsers((prevUsers) =>
                    isNewFilter ? response.users : [...prevUsers, ...response.users],
                );
                setHasNextPage(response.hasNextPage);
                setLastEvaluatedKey(response.lastEvaluatedKey || undefined);
            } catch (err) {
                setError("Failed to fetch users. Please try again later.");
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        },
        [lastEvaluatedKey],
    );

    // Effect for when the selected letter or the debounced search term changes
    useEffect(() => {
        // If a search term exists, it takes precedence.
        if (debouncedSearchTerm) {
            // Ensure letter selection is cleared when searching.
            if (selectedLetter !== "") setSelectedLetter("");
            fetchUsers("", true, debouncedSearchTerm);
        } else {
            // Otherwise, fetch based on the selected letter.
            // This also handles the initial load since selectedLetter defaults to "A".
            fetchUsers(selectedLetter || "A", true, "");
        }
        // The dependency array is carefully constructed to avoid infinite loops.
        // We only want to re-run this logic when the primary filters change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLetter, debouncedSearchTerm]);

    const handleLetterSelect = (letter: string) => {
        if (letter !== selectedLetter) {
            setSearchTerm(""); // Clear search term
            setUsers([]); // Clear users to show loading indicator
            setSelectedLetter(letter);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const loadMoreUsers = () => {
        if (!isLoading && hasNextPage) {
            fetchUsers(selectedLetter, false, debouncedSearchTerm);
        }
    };

    return (
        <Container maxWidth="lg" className={classes.container}>
            <Box>
                <Typography variant="h4" component="h1" className={classes.pageTitle}>
                    Members Directory
                </Typography>
                {/* <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Connect with professionals in the community.
                </Typography> */}

                {/* Search Bar */}
                <Box className={classes.searchContainer}>
                    <TextField
                        className={classes.searchInput}
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name, title, or keyword..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.primary" }} />,
                        }}
                    />
                </Box>
            </Box>

            <Box className={classes.directoryContent}>
                {/* Alphabet Navigator - hidden when searching */}
                {!searchTerm && (
                    <Box className={classes.navigatorContainer}>
                        <AlphabetNavigator
                            activeLetter={selectedLetter}
                            onLetterSelect={handleLetterSelect}
                        />
                    </Box>
                )}

                {/* Main Content */}
                <Box className={classes.usersContainer}>
                    {/* Section Header */}
                    {!searchTerm && selectedLetter && users.length > 0 && (
                        <Typography variant="h4" component="h2" className={classes.letterHeading}>
                            {selectedLetter}
                        </Typography>
                    )}

                    {/* Loading indicator for new filter/search */}
                    {isLoading && users.length === 0 && (
                        <Box className={classes.fullPageLoader}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Loading Professionals...</Typography>
                        </Box>
                    )}

                    {users.length > 0 && (
                        <InfiniteScroll
                            dataLength={users.length}
                            next={loadMoreUsers}
                            hasMore={hasNextPage}
                            loader={
                                <Box className={classes.loader}>
                                    <CircularProgress />
                                </Box>
                            }
                            endMessage={
                                <Typography className={classes.endMessage}>
                                    You&apos;ve reached the end of the this letter.
                                </Typography>
                            }
                            className={classes.infiniteScrollContainer}
                        >
                            <Grid container spacing={3}>
                                {users.map((user) => (
                                    <Grid item key={user.id} xs={12} sm={6} md={4}>
                                        <ProfileCard user={user} />
                                    </Grid>
                                ))}
                            </Grid>
                        </InfiniteScroll>
                    )}

                    {/* No Results and Error States */}
                    {!isLoading && users.length === 0 && !error && (
                        <Box className={classes.noResults}>
                            <Typography variant="h6">No Professionals Found</Typography>
                            <Typography color="textSecondary">
                                Try adjusting your search or selecting a different letter.
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Box className={classes.noResults}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export default UserDirectoryPage;
